import { useContext, useEffect, useId, useRef, useCallback } from "react";
import { ToolbarItemProps } from "../types";
import { ToolbarContext } from "./ToolbarContext";
import { useIntersectionObserver } from "../../../../internal/hooks";
import { updateToolbarItemsTabIndex } from "./utils/accessibility";

import cx from "classnames";
import styles from "../Toolbar.module.scss";

/**
 * Props for the ToolbarItemWrapper component
 */
export type ToolbarItemWrapperProps = {
  /**
   * Toolbar item configuration without ID (ID is generated internally)
   */
  item: Omit<ToolbarItemProps, "id">;
  /**
   * The toolbar item component to wrap
   */
  children: React.ReactNode;
  /** Optional override for the intersection observer root element */
  observerRoot?: Element | null;
  /** Optional override for visibility change handling */
  onVisibilityChange?: (isVisible: boolean) => void;
  /** Optional margin offset for the intersection observer root */
  rootMargin?: string;
  /** Optional class name to apply to the wrapper element */
  className?: string;
};

/**
 * ToolbarItemWrapper manages item visibility and focus behavior within a toolbar.
 *
 * Features:
 * - Intersection observation for overflow detection
 * - Automatic overflow menu integration in collapse mode
 * - Focus management and keyboard navigation
 * - ARIA compliance for accessibility
 * - Visibility state management
 * - Proper tab index handling
 * - Context integration with toolbar state
 *
 * @example
 * <ToolbarItemWrapper item={{ itemType: "button", itemProps: buttonProps }}>
 *   <ToolbarButton {...buttonProps} />
 * </ToolbarItemWrapper>
 */
export const ToolbarItemWrapper = ({
  item,
  children,
  observerRoot,
  onVisibilityChange,
  rootMargin,
  className,
}: ToolbarItemWrapperProps) => {
  const { addItem, removeItem, toolbarRef, overflow } =
    useContext(ToolbarContext);
  const itemId = useId();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const isCollapseMode = overflow === "collapse";
  const prevIntersectingRef = useRef<boolean>(true); // Start as visible

  /**
   * Handles visibility changes from intersection observer in collapse mode
   * In collapse mode:
   * - When item becomes visible, removes it from overflow menu
   * - When item becomes hidden, adds it to overflow menu
   */
  const handleVisibilityChange = useCallback(
    (isVisible: boolean) => {
      if (!isCollapseMode || prevIntersectingRef.current === isVisible) return;

      if (onVisibilityChange) {
        onVisibilityChange(isVisible);
      } else if (item) {
        const itemWithId = { ...item, id: itemId };
        if (isVisible) {
          removeItem(itemWithId);
        } else {
          addItem(itemWithId);
        }
      }
      prevIntersectingRef.current = isVisible;
    },
    [isCollapseMode, onVisibilityChange, item, itemId, addItem, removeItem],
  );

  const { isIntersecting, ref } = useIntersectionObserver({
    root: isCollapseMode ? (observerRoot ?? toolbarRef.current) : null,
    threshold: 1,
    initialIsIntersecting: true, // Start as visible
    onChange: handleVisibilityChange,
    rootMargin,
  });

  const setRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      elementRef.current = node;
      // Only set up intersection observer in collapse mode
      if (isCollapseMode) {
        ref(node);
      }
    },
    [ref, isCollapseMode],
  );

  /**
   * Manages focus behavior between toolbar items using accessibility utils
   * - Uses updateToolbarItemsTabIndex to manage focus state
   * - Ensures proper keyboard navigation support
   * - Maintains ARIA compliance for focus management
   */
  const handleFocusOrClick = useCallback(() => {
    if (!toolbarRef.current || !elementRef.current) return;

    const currentElement = elementRef.current.querySelector("button, a, input");
    if (!currentElement) return;

    updateToolbarItemsTabIndex(toolbarRef.current, currentElement);
  }, [toolbarRef]);

  // Set up focus management
  useEffect(() => {
    const interactiveEl = elementRef.current?.querySelector("button, a, input");
    if (!interactiveEl) return;

    interactiveEl.addEventListener("focus", handleFocusOrClick);
    interactiveEl.addEventListener("click", handleFocusOrClick);

    return () => {
      interactiveEl.removeEventListener("focus", handleFocusOrClick);
      interactiveEl.removeEventListener("click", handleFocusOrClick);
    };
  }, [handleFocusOrClick]);

  const toolbarItemClassName = cx(styles["toolbar-item"], className);
  const isHidden = isCollapseMode && !isIntersecting;

  return (
    <div
      ref={setRefCallback}
      className={toolbarItemClassName}
      style={{ visibility: isHidden ? "hidden" : "visible" }}
      aria-hidden={isHidden}
      {...{ inert: isHidden ? "" : undefined }}
      data-id={itemId}
      data-anv="toolbar-item"
    >
      {children}
    </div>
  );
};
